import { MaterialIcon, MaterialIconName } from 'client/shared/components/base';
import { AiAssistantQueryStatusType } from 'client/admin/core';
import { LoadedAdminData } from 'client/admin/shared/types';
import * as React from 'react';
import { AiAssistant, AiAssistantName } from 'core';
import { MainChatContainer } from '../main-chat-container';
import { useAiAssistantSession } from '../../hooks/use-ai-assistant-session';
import { AdminError, ErrorType } from 'client/admin/shared/components/error';
import { AdminPageLoader } from 'client/admin/shared/components/page-loader';
import { RemainingQuestionLimit } from '../../components/remaining-questions-banner';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useSandboxAiAssistantId } from '../../hooks/use-sandbox-ai-assistant-id';
import { usePublisherAiAssistant } from '../../hooks/use-publisher-ai-assistant';
import { ClientUrlUtils } from 'client/shared/core/helpers';
import { useQueryParameter, useRedirect } from 'client/shared/hooks';

export enum AiAssistantDisableReason {
  EXPIRED_SESSION = 'EXPIRED_SESSION',
  QUERY_LIMIT_REACHED = 'QUERY_LIMIT_REACHED',
  NOT_FOUND_SESSION = 'NOT_FOUND_SESSION',
}

export const AiAssistantContainer: React.FC<{
  readonly adminData: LoadedAdminData;
  readonly assistant: AiAssistant;
  readonly headerBackgroundColor?: string;
  readonly headerTypographyVariant: 'h1' | 'h3';
  readonly onOpenFullScreen?: () => void;
  readonly onCloseDrawer?: () => void;
}> = (p) => {
  const [sandboxAssistantId, setSandboxAssistantId] = useSandboxAiAssistantId();
  const pubAssistant = usePublisherAiAssistant(
    p.adminData.admin.activePublishingEntity.id,
    p.assistant
  );
  const assistant: AiAssistant =
    p.assistant.type === 'OpenAIAssistant'
      ? { ...p.assistant, id: sandboxAssistantId }
      : p.assistant;
  const params = useQueryParameter();

  const {
    aiAssistantMessages,
    loading,
    states,
    events,
    aiAssistantDisableReason,
    isMessageInputDisabled,
    queryLimit,
    showQueryLimit,
    messageContent,
    setMessageContent,
  } = useAiAssistantSession(assistant);

  const redirect = useRedirect();
  const historyAssistantFilter =
    p.assistant.type === 'PubAssistant'
      ? pubAssistant.assistant?.id
      : p.assistant.id;

  const refreshAndRefetch = (sessionIdToResume?: string) => {
    params.changeQueryParam('session_id', null);
    setMessageContent('');
    void events.refreshSessionId(sessionIdToResume);
  };

  if (loading) {
    return <AdminPageLoader />;
  } else if (!states) {
    return <AdminError errorType={ErrorType.NOT_FOUND} />;
  }

  const { isOnSendInProgress, isOnRefreshInProgress } = states.events;
  const { queryStatus } = states.session;

  const requestInProcess =
    queryStatus.value?.type === AiAssistantQueryStatusType.PROCESSING ||
    isOnSendInProgress.value;

  return (
    <Stack
      height="100%"
      sx={{
        // FIX: This is what shortens the height of the chat box,
        // causes the wonky infinite scroll,
        // and cuts off the bottom of the message list in a tall drawer chat.
        '.cs-message-list__scroll-wrapper': {
          maxHeight: 'calc(67vh - 65px)',
        },
        // TODO:
        // All avatars have an active `width: 42px` but it only affects some of them... others `flex-shrink`.
        // Just override it here until we can find the real reason.
        // Something to do with `Avatar.size` - we set it to `sm` but the width doesn't always adjust accordingly.
        // When not `sm`, everything looks fine.
        '.cs-message__avatar': {
          // The avatars are 26px except for our user/sender avatar, which is 30px and gets cut off otherwise.
          // Should be fixed separately.
          minWidth: '30px',
          width: '30px',
        },
      }}
      width="100%"
    >
      <Stack gap={2} py={2}>
        <Stack
          alignItems="center"
          color={(theme) =>
            theme.palette.getContrastText(
              p.headerBackgroundColor ?? theme.palette.background.default
            )
          }
          direction="row"
          justifyContent="space-between"
          px={3}
        >
          <Typography
            mt={0.5} // Align with the icons; alignItems doesn't quite do it
            variant={p.headerTypographyVariant}
          >
            {pubAssistant.assistant?.name ??
              headerByAssistant[p.assistant.id] ??
              'Ask Polco AI'}
          </Typography>
          <Stack direction="row">
            <Button
              aria-label="history"
              color="inherit"
              onClick={() =>
                redirect(
                  ClientUrlUtils.admin.aiHistory.path(historyAssistantFilter),
                  {
                    push: true,
                  }
                )
              }
              startIcon={<MaterialIcon icon={MaterialIconName.HISTORY} />}
            >
              History
            </Button>
            <Button
              aria-label="new"
              color="inherit"
              disabled={loading || requestInProcess}
              onClick={() => refreshAndRefetch()}
              startIcon={<MaterialIcon icon={MaterialIconName.REFRESH} />}
            >
              New Session
            </Button>
            {p.onOpenFullScreen && (
              <IconButton
                aria-label="open in fullscreen"
                color="inherit"
                onClick={p.onOpenFullScreen}
              >
                <MaterialIcon icon={MaterialIconName.OPEN_IN_NEW} />
              </IconButton>
            )}
            {p.onCloseDrawer && (
              <IconButton
                aria-label="close drawer"
                color="inherit"
                onClick={p.onCloseDrawer}
              >
                <MaterialIcon icon={MaterialIconName.CLOSE} />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {p.assistant.id === AiAssistantName.SANDBOX && (
          <TextField
            disabled={states.session.conversation.messages.length > 1}
            label="OpenAI Assistant ID"
            onChange={(e) => setSandboxAssistantId(e.target.value)}
            placeholder="asst_IhKx86hnkZzvwwPhYrjwjSrG"
            value={sandboxAssistantId ?? ''}
          />
        )}
      </Stack>
      <RemainingQuestionLimit
        adminData={p.adminData}
        queryCount={states?.publisher.queryCount}
        queryLimit={queryLimit}
        showQueryLimit={showQueryLimit}
      />
      <MainChatContainer
        adminData={p.adminData}
        aiAssistantDisableReason={aiAssistantDisableReason}
        aiAssistantMessages={aiAssistantMessages}
        assistant={p.assistant}
        events={events}
        isLoading={isOnRefreshInProgress.value}
        isMessageInputDisabled={isMessageInputDisabled}
        loadingText={queryStatus.value?.loadingText}
        messageContent={messageContent}
        messageInputEvents={{
          setMessageContent,
          onSend: (onSendComplete?: () => void) =>
            events.onSend(messageContent, () => {
              setMessageContent('');
              onSendComplete?.();
            }),
        }}
        queryLimit={queryLimit}
        refreshAndRefetch={refreshAndRefetch}
        requestInProcess={requestInProcess}
        sessionId={states.session.id}
        showCancelQuery={
          queryStatus.value?.type === AiAssistantQueryStatusType.PROCESSING &&
          !isOnSendInProgress.value
        }
        showQueryLimit={showQueryLimit && !aiAssistantDisableReason}
      />
    </Stack>
  );
};
AiAssistantContainer.displayName = 'AiAssistantContainer';

const headerByAssistant: Record<string, string> = {
  [AiAssistantName.POLLY]: 'Ask Polly AI',
  [AiAssistantName.GRANT_WRITER]: 'Ask Grant Writer AI',
  [AiAssistantName.ANNIE]: 'Ask Annie AI',
  [AiAssistantName.SANDBOX]: 'AI Assistant Preview',
};
